<template>
  <UInputMenu
    ref="inputMenu"
    v-bind="{ ...forwardedProps, ...$attrs }"
    :loading="false"
    :aria-busy="loading || undefined"
    class="p-input-menu"
    @update:model-value="emit('update:modelValue', $event)"
    @update:open="emit('update:open', $event)"
    v-model:search-term="searchTerm"
    @highlight="emit('highlight', $event)"
    @create="emit('create', $event)"
    @remove-tag="emit('remove-tag', $event)"
    @change="emit('change', $event)"
    @blur="emit('blur', $event)"
    @focus="emit('focus', $event)"
  >
    <template v-if="slots.leading || leadingIcon" #leading="slotProps">
      <slot name="leading" v-bind="slotProps">
        <FontAwesomeIcon
          v-if="leadingIcon"
          :icon="leadingIcon"
          aria-hidden="true"
          data-slot="leadingIcon"
          :class="[
            slotProps.ui.leadingIcon({ class: props.ui?.leadingIcon }),
            { 'animate-spin': loading }
          ]"
        />
      </slot>
    </template>
    <template v-if="slots.default" #default="slotProps"><slot v-bind="slotProps" /></template>
    <template v-if="slots.trailing || trailingIcon || clear" #trailing="slotProps">
      <slot name="trailing" v-bind="slotProps">
        <component
          :is="mode === 'autocomplete' ? AutocompleteCancel : ComboboxCancel"
          v-if="clear && !disabled && hasValue(slotProps.modelValue)"
          as-child
        >
          <PButton
            as="span"
            :size="size"
            variant="link"
            color="secondary"
            tabindex="-1"
            v-bind="clearProps"
            data-slot="trailingClear"
            :class="slotProps.ui.trailingClear({ class: props.ui?.trailingClear })"
            @click.stop="emit('clear')"
          >
            <FontAwesomeIcon :icon="(clearIcon || faXmark) as IconProp" aria-hidden="true" />
          </PButton>
        </component>
        <FontAwesomeIcon
          v-else-if="trailingIcon"
          :icon="trailingIcon"
          aria-hidden="true"
          data-slot="trailingIcon"
          :class="[
            slotProps.ui.trailingIcon({ class: props.ui?.trailingIcon, leading: isLeading }),
            { 'animate-spin': loading && !isLeading }
          ]"
        />
      </slot>
    </template>
    <template #item="slotProps">
      <slot name="item" v-bind="slotProps">
        <slot name="item-leading" v-bind="slotProps">
          <FontAwesomeIcon
            v-if="itemIcon(slotProps.item)"
            :icon="itemIcon(slotProps.item)!"
            aria-hidden="true"
            data-slot="itemLeadingIcon"
            :class="
              slotProps.ui.itemLeadingIcon({
                class: [props.ui?.itemLeadingIcon, itemObject(slotProps.item)?.ui?.itemLeadingIcon]
              })
            "
          />
          <UAvatar
            v-else-if="itemObject(slotProps.item)?.avatar"
            :size="
              (itemObject(slotProps.item)?.ui?.itemLeadingAvatarSize ||
                props.ui?.itemLeadingAvatarSize ||
                slotProps.ui.itemLeadingAvatarSize()) as AvatarProps['size']
            "
            v-bind="itemObject(slotProps.item)?.avatar"
            data-slot="itemLeadingAvatar"
            :class="
              slotProps.ui.itemLeadingAvatar({
                class: [
                  props.ui?.itemLeadingAvatar,
                  itemObject(slotProps.item)?.ui?.itemLeadingAvatar
                ]
              })
            "
          />
          <UChip
            v-else-if="itemObject(slotProps.item)?.chip"
            :size="
              (itemObject(slotProps.item)?.ui?.itemLeadingChipSize ||
                props.ui?.itemLeadingChipSize ||
                slotProps.ui.itemLeadingChipSize()) as ChipProps['size']
            "
            inset
            standalone
            v-bind="itemObject(slotProps.item)?.chip"
            data-slot="itemLeadingChip"
            :class="
              slotProps.ui.itemLeadingChip({
                class: [props.ui?.itemLeadingChip, itemObject(slotProps.item)?.ui?.itemLeadingChip]
              })
            "
          />
        </slot>
        <span
          data-slot="itemWrapper"
          :class="
            slotProps.ui.itemWrapper({
              class: [props.ui?.itemWrapper, itemObject(slotProps.item)?.ui?.itemWrapper]
            })
          "
        >
          <span
            data-slot="itemLabel"
            :class="
              slotProps.ui.itemLabel({
                class: [props.ui?.itemLabel, itemObject(slotProps.item)?.ui?.itemLabel]
              })
            "
          >
            <slot name="item-label" :item="slotProps.item" :index="slotProps.index">
              {{
                itemObject(slotProps.item)
                  ? get(itemObject(slotProps.item), props.labelKey ?? "label")
                  : slotProps.item
              }}
            </slot>
          </span>
          <span
            v-if="
              itemObject(slotProps.item) &&
              (get(itemObject(slotProps.item), props.descriptionKey ?? 'description') ||
                slots['item-description'])
            "
            data-slot="itemDescription"
            :class="
              slotProps.ui.itemDescription({
                class: [props.ui?.itemDescription, itemObject(slotProps.item)?.ui?.itemDescription]
              })
            "
          >
            <slot name="item-description" :item="slotProps.item" :index="slotProps.index">
              {{ get(itemObject(slotProps.item), props.descriptionKey ?? "description") }}
            </slot>
          </span>
        </span>
        <span
          data-slot="itemTrailing"
          :class="
            slotProps.ui.itemTrailing({
              class: [props.ui?.itemTrailing, itemObject(slotProps.item)?.ui?.itemTrailing]
            })
          "
        >
          <slot name="item-trailing" v-bind="slotProps" />
          <ComboboxItemIndicator v-if="mode !== 'autocomplete'" as-child>
            <FontAwesomeIcon
              :icon="(selectedIcon || faCheck) as IconProp"
              aria-hidden="true"
              data-slot="itemTrailingIcon"
              :class="
                slotProps.ui.itemTrailingIcon({
                  class: [
                    props.ui?.itemTrailingIcon,
                    itemObject(slotProps.item)?.ui?.itemTrailingIcon
                  ]
                })
              "
            />
          </ComboboxItemIndicator>
        </span>
      </slot>
    </template>
    <template #tags-item-delete="slotProps">
      <slot name="tags-item-delete" v-bind="slotProps">
        <FontAwesomeIcon
          :icon="(deleteIcon || faXmark) as IconProp"
          aria-hidden="true"
          data-slot="tagsItemDeleteIcon"
          :class="
            slotProps.ui.tagsItemDeleteIcon({
              class: [
                props.ui?.tagsItemDeleteIcon,
                itemObject(slotProps.item)?.ui?.tagsItemDeleteIcon
              ]
            })
          "
        />
      </slot>
    </template>
    <template v-if="slots['tags-item-text']" #tags-item-text="slotProps"
      ><slot name="tags-item-text" v-bind="slotProps"
    /></template>
    <template v-if="slots['create-item-label']" #create-item-label="slotProps"
      ><slot name="create-item-label" v-bind="slotProps"
    /></template>
    <template v-if="slots.empty" #empty="slotProps"
      ><slot name="empty" v-bind="slotProps"
    /></template>
    <template v-if="slots['content-top']" #content-top="slotProps"
      ><slot name="content-top" v-bind="slotProps"
    /></template>
    <template v-if="slots['content-bottom']" #content-bottom="slotProps"
      ><slot name="content-bottom" v-bind="slotProps"
    /></template>
  </UInputMenu>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends ArrayOrNested<InputMenuItem> = ArrayOrNested<InputMenuItem>,
    VK extends GetItemKeys<T> | undefined = undefined,
    M extends boolean = false,
    Mod extends Omit<ModelModifiers, 'lazy'> = Omit<ModelModifiers, 'lazy'>,
    C extends boolean | object = false
  "
>
import { computed, useTemplateRef } from "vue"
import NuxtInputMenu, {
  type InputMenuItem,
  type InputMenuEmits,
  type InputMenuSlots
} from "@nuxt/ui/components/InputMenu.vue"
import type { ArrayOrNested, GetItemKeys, ModelModifiers } from "@nuxt/ui"
import type { ComponentExposed } from "vue-component-type-helpers"
import { faCheck, faChevronDown, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { get } from "@nuxt/ui/utils"
import { ComboboxItemIndicator, ComboboxCancel, AutocompleteCancel } from "reka-ui"
import PButton from "@/components/elements/PButton.vue"
import UAvatar, { type AvatarProps } from "@nuxt/ui/components/Avatar.vue"
import UChip, { type ChipProps } from "@nuxt/ui/components/Chip.vue"
import type { PInputMenuProps } from "./input-menu"

const UInputMenu = NuxtInputMenu<T, VK, M, Mod, C>
const props = withDefaults(defineProps<PInputMenuProps<T, VK, M, Mod, C>>(), {
  color: "secondary",
  variant: "outline",
  resetSearchTermOnBlur: undefined,
  resetSearchTermOnSelect: undefined,
  resetModelValueOnClear: undefined,
  highlightOnHover: undefined,
  loop: undefined,
  openOnClick: undefined,
  openOnFocus: undefined,
  ignoreFilter: undefined,
  open: undefined,
  defaultOpen: undefined,
  multiple: undefined,
  portal: undefined,
  arrow: undefined,
  disabled: undefined,
  required: undefined,
  highlight: undefined,
  fixed: undefined,
  autofocus: undefined,
  leading: undefined,
  trailing: undefined,
  loading: undefined
})
const emit = defineEmits<InputMenuEmits<T, VK, M, Mod, C>>()
const searchTerm = defineModel<string>("searchTerm", { default: "" })
const slots = defineSlots<InputMenuSlots<T, VK, M, Mod, C>>()
const forwardedProps = computed(() => ({
  ...props,
  icon: undefined,
  leadingIcon: undefined,
  trailingIcon: undefined,
  loadingIcon: undefined,
  selectedIcon: undefined,
  clearIcon: undefined,
  deleteIcon: undefined
}))
const isLeading = computed(() =>
  Boolean(
    props.leadingIcon ||
    (props.icon && (props.leading || !props.trailing)) ||
    (props.loading && !props.trailing)
  )
)
const leadingIcon = computed(() =>
  isLeading.value
    ? ((props.loading
        ? props.loadingIcon || faSpinner
        : props.leadingIcon || props.icon) as IconProp)
    : undefined
)
const trailingIcon = computed(() =>
  props.trailing !== false
    ? ((props.loading && !isLeading.value
        ? props.loadingIcon || faSpinner
        : props.trailingIcon || (props.trailing && props.icon) || faChevronDown) as IconProp)
    : undefined
)
function itemObject(item: InputMenuItem) {
  return typeof item === "object" && item !== null ? item : undefined
}
function itemIcon(item: InputMenuItem) {
  return itemObject(item)?.icon as IconProp | undefined
}
const clearProps = computed(() => {
  if (typeof props.clear !== "object") return {}
  const forwarded = { ...(props.clear as Record<string, unknown>) }
  for (const key of ["icon", "leadingIcon", "trailingIcon", "loadingIcon"]) delete forwarded[key]
  return forwarded
})
function hasValue(value: unknown) {
  return Array.isArray(value)
    ? value.length > 0
    : value !== undefined && value !== null && value !== ""
}
const inputMenu = useTemplateRef<ComponentExposed<typeof UInputMenu>>("inputMenu")
defineOptions({ inheritAttrs: false })
defineExpose({
  get inputRef() {
    return inputMenu.value!.inputRef
  },
  get viewportRef() {
    return inputMenu.value!.viewportRef
  }
})
</script>
